import React, { useEffect, useState } from 'react';
import logo from './assets/logo.jpg';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { getCartItemCount } from './services/cartService';

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);

  useEffect(() => {
    setCartItemCount(getCartItemCount());
  }, [location]);
  
  const menu = [
    { name: t('header.home'), path: '/' },
    { name: t('header.about'), path: '/about' },
    { name: t('header.products'), path: '/products' },
    { name: t('header.testimonials'), path: '/testimonials' },
    // { name: t('header.contact'), path: '/contact' },
  ];
  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full border-b border-gray-200 bg-white/95 shadow-sm backdrop-blur">
      <div className="mx-auto flex h-20 w-full max-w-7xl items-center justify-between px-4 md:px-8">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-3 text-primary" onClick={() => setMenuOpen(false)}>
          <img src={logo} alt={t('header.logoAlt')} className="h-10 w-10 rounded-full object-cover" />
          <span className="hidden text-xl font-bold tracking-tight sm:inline">MMK Industries</span>
        </Link>

        {/* Desktop navigation */}
        <nav className="hidden items-center gap-2 md:flex">
          {menu.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors duration-200 ${
                location.pathname === item.path
                  ? 'bg-primary/10 text-primary'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-primary'
              }`}
            >
              {item.name}
            </Link>
          ))}
        </nav>

        {/* Desktop actions */}
        <div className="hidden items-center gap-2 md:flex">
          <LanguageSwitcher />
          <Link
            to="/cart"
            className="relative rounded-full p-2 text-gray-700 transition-colors hover:bg-gray-100 hover:text-primary"
            aria-label={t('subHeader.cart')}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l2.2 10.4a2 2 0 0 0 2 1.6h7.6a2 2 0 0 0 2-1.6L21 6H7" />
              <circle cx="10" cy="19" r="1.5" />
              <circle cx="17" cy="19" r="1.5" />
            </svg>
            {cartItemCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white">
                {cartItemCount}
              </span>
            )}
          </Link>
          <Link
            to="/check-order-status"
            className="rounded-md border border-primary px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
          >
            {t('header.checkOrder')}
          </Link>
          <Link
            to="/contact"
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-900"
          >
            {t('header.contact')}
          </Link>
        </div>

        {/* Mobile actions */}
        <div className="flex items-center gap-1 md:hidden">
          <Link
            to="/cart"
            className="relative rounded-full p-2.5 text-gray-700 transition-colors hover:bg-gray-100 hover:text-primary"
            aria-label={t('subHeader.cart')}
            onClick={() => setMenuOpen(false)}
          >
            <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l2.2 10.4a2 2 0 0 0 2 1.6h7.6a2 2 0 0 0 2-1.6L21 6H7" />
              <circle cx="10" cy="19" r="1.5" />
              <circle cx="17" cy="19" r="1.5" />
            </svg>
            {cartItemCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white">
                {cartItemCount}
              </span>
            )}
          </Link>
          <button
            className="rounded-md p-2.5 text-primary transition-colors hover:bg-gray-100"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={t('header.toggleMenu')}
          >
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {menuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {menuOpen && (
        <nav className="border-t border-gray-200 bg-white px-4 py-4 shadow-sm md:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-2">
            {menu.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`rounded-md px-3 py-3 text-base font-semibold transition-colors ${
                  location.pathname === item.path
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-primary'
                }`}
                onClick={() => setMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <div className="mt-3 flex flex-col gap-2 border-t border-gray-200 pt-3">
              <div className="flex items-center justify-between">
                <LanguageSwitcher />
                <Link
                  to="/cart"
                  className="relative rounded-full p-2.5 text-gray-700 transition-colors hover:bg-gray-100"
                  onClick={() => setMenuOpen(false)}
                  aria-label={t('subHeader.cart')}
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l2.2 10.4a2 2 0 0 0 2 1.6h7.6a2 2 0 0 0 2-1.6L21 6H7" />
                    <circle cx="10" cy="19" r="1.5" />
                    <circle cx="17" cy="19" r="1.5" />
                  </svg>
                  {cartItemCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white">
                      {cartItemCount}
                    </span>
                  )}
                </Link>
              </div>
              <Link
                to="/check-order-status"
                className="rounded-md border border-primary px-4 py-3 text-center text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
                onClick={() => setMenuOpen(false)}
              >
                {t('header.checkOrder')}
              </Link>
              <Link
                to="/contact"
                className="rounded-md bg-primary px-4 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-blue-900"
                onClick={() => setMenuOpen(false)}
              >
                {t('header.contact')}
              </Link>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header; 